import { useState } from "react";
import { Check, ImagePlus, Link2, Loader2, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, fileUrl } from "@/lib/api";

const Uploader = ({ onAdd }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const fd = new FormData();
    [...files].forEach((f) => fd.append("files", f));
    try { const { data } = await api.post("/uploads", fd); onAdd(data); toast.success(`${data.length} photo(s) added`); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <label onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); upload(e.dataTransfer.files); }}
      className="flex flex-col items-center justify-center gap-3 h-56 border border-dashed border-[var(--ink)]/30 bg-[var(--cream)] cursor-pointer hover:border-[var(--terracotta)] transition-colors" data-testid="upload-dropzone">
      {busy ? <Loader2 className="animate-spin" /> : <ImagePlus size={28} strokeWidth={1.3} />}
      <span className="text-sm font-medium">{busy ? "Uploading…" : "Drop product photos or click to browse"}</span>
      <span className="text-xs text-[var(--muted-ink)]">Lifestyle, studio, configurations, fabric close-ups</span>
      <input type="file" multiple accept="image/*" className="hidden" onChange={(e) => upload(e.target.files)} data-testid="upload-input" />
    </label>
  );
};

const LinkImporter = ({ p, update, cat, onAdd }) => {
  const [url, setUrl] = useState(p.source_url || "");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [picked, setPicked] = useState(new Set());

  const fetchLink = async () => {
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post("/scrape", { url, category_id: cat.id });
      setRes(data);
      setPicked(new Set(data.candidates.filter((c) => c.is_product).map((c) => c.path)));
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  const toggle = (path) => setPicked((s) => { const n = new Set(s); n.has(path) ? n.delete(path) : n.add(path); return n; });
  const addPicked = () => {
    onAdd(res.candidates.filter((c) => picked.has(c.path)).map(({ path, width, height }) => ({ path, width, height })));
    const d = res.details || {};
    const patch = { source_url: url };
    if (!p.name && d.name) patch.name = d.name;
    if (!p.description && d.description) patch.description = d.description;
    if (!p.materials && d.materials) patch.materials = d.materials;
    if (!p.highlights.length && d.highlights?.length) patch.highlights = d.highlights;
    if (!p.configurations.length && d.configurations?.length) {
      const opts = cat.configurations || [];
      const match = (n) => opts.find((o) => o.toLowerCase().replace(/\s+/g, "") === String(n || "").toLowerCase().replace(/\s+/g, ""));
      const rows = d.configurations.map((c) => ({ ...c, name: match(c.name) })).filter((c) => c.name);
      if (rows.length) patch.configurations = rows;
    }
    update(patch);
    toast.success(`${picked.size} photos kept${d.name ? " · details pre-filled" : ""}`);
    setRes(null);
  };

  return (
    <div className="bg-[var(--cream)] p-6 border border-[var(--line)]">
      <div className="flex gap-2">
        <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://store.com/products/runa-sofa" className="h-11" data-testid="link-url-input" />
        <Button onClick={fetchLink} disabled={!url.startsWith("http") || busy} className="h-11" data-testid="link-fetch-button">
          {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Link2 size={14} className="mr-2" />}Fetch
        </Button>
      </div>
      {busy && <p className="text-xs text-[var(--muted-ink)] mt-3">Downloading images and asking AI which ones are product photos…</p>}
      {res && (
        <div className="mt-6" data-testid="link-candidates">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm"><Sparkles size={13} className="inline mr-1 text-[var(--terracotta)]" />{res.candidates.length} images found · AI pre-selected product photos</p>
            <Button size="sm" onClick={addPicked} disabled={!picked.size} data-testid="link-add-selected-button">Keep {picked.size} selected</Button>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            {res.candidates.map((c, i) => (
              <button key={c.path} onClick={() => toggle(c.path)} data-testid={`candidate-${i}`}
                className={`relative aspect-square overflow-hidden border-2 transition-all ${picked.has(c.path) ? "border-[var(--terracotta)]" : "border-transparent opacity-50 hover:opacity-80"}`}>
                <img src={fileUrl(c.path, 300)} alt={c.label} className="w-full h-full object-cover" />
                {picked.has(c.path) && <span className="absolute top-1 right-1 bg-[var(--terracotta)] text-white p-0.5"><Check size={12} /></span>}
                <span className={`absolute bottom-0 inset-x-0 text-[9px] uppercase tracking-wider px-1 py-0.5 text-left truncate ${c.is_product ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-[var(--line)] text-[var(--ink)]"}`}>
                  {c.is_product ? c.label || "product" : "not product"}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export const PhotosStep = ({ p, update, cat }) => {
  const add = (refs) => update({ images: [...p.images, ...refs.filter((r) => !p.images.some((i) => i.path === r.path))] });
  const remove = (path) => update({ images: p.images.filter((i) => i.path !== path) });
  return (
    <div className="grid lg:grid-cols-2 gap-10">
      <div className="space-y-8">
        <div><h2 className="text-base md:text-lg font-bold mb-3">Upload photos</h2><Uploader onAdd={add} /></div>
        <div><h2 className="text-base md:text-lg font-bold mb-3">…or import from a product link</h2><LinkImporter p={p} update={update} cat={cat} onAdd={add} /></div>
      </div>
      <div>
        <h2 className="text-base md:text-lg font-bold mb-1">Selected for this product <span className="text-[var(--muted-ink)] font-normal">({p.images.length})</span></h2>
        <p className="text-xs text-[var(--muted-ink)] mb-4">The image page has 6 frames: 1 hero, 4 grid squares, 1 tall detail. Fewer photos are fine — AI will split them into detail crops.</p>
        {p.images.length === 0 ? <div className="h-56 grid place-items-center border border-[var(--line)] text-sm text-[var(--muted-ink)]" data-testid="no-images">No photos yet</div> : (
          <div className="grid grid-cols-3 gap-3" data-testid="selected-images">
            {p.images.map((img, i) => (
              <div key={img.path} className="relative aspect-square overflow-hidden group bg-[var(--line)] rise" style={{ animationDelay: `${i * 40}ms` }}>
                <img src={fileUrl(img.path, 400)} alt="" className="w-full h-full object-cover" />
                <button onClick={() => remove(img.path)} className="absolute top-1 right-1 bg-[var(--cream)] p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`remove-image-${i}`}><X size={12} /></button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
